import type { AuthedRequest } from '../auth/AuthProvider';

/** Mirrors the hold DTO of /api/v1/holds (ADR-015). */
export interface Hold {
  id: string;
  /** name is "" when the device was deleted: evidence outlives it. */
  device: { id: string; name: string };
  from: string;
  to: string;
  reason: string;
  created_by: { id: string; name: string };
  created_at: string;
  /** Still protecting its range. */
  active: boolean;
  /** active, expired (past its validity) or released. */
  status: HoldStatus;
  /** End of the protection; null for evidence made before validity existed. */
  expires_at: string | null;
  released_at: string | null;
  released_by: { id: string; name: string } | null;
  release_reason: string | null;
  /** Recordings the hold covers right now. */
  segments: number;
  bytes: number;
}

export type HoldStatus = 'active' | 'expired' | 'released';

/** Longest evidence, and the validities offered (scope review of 08/10/2026). */
export const HOLD_MAX_MS = 30 * 60_000;
export const holdValidityDays = [30, 90, 180, 365] as const;

export interface HoldPage {
  items: Hold[];
  total: number;
  limit: number;
  offset: number;
}

export interface HoldQuery {
  deviceId?: string;
  active?: boolean;
  from?: number;
  to?: number;
  page?: number;
  size?: number;
}

export const holdsKey = ['holds'] as const;

const iso = (t: number) => new Date(t).toISOString().replace('.000Z', 'Z');

export function listHolds(req: AuthedRequest, q: HoldQuery): Promise<HoldPage> {
  const p = new URLSearchParams();
  if (q.deviceId) p.set('device_id', q.deviceId);
  if (q.active) p.set('active', 'true');
  if (q.from !== undefined && q.to !== undefined) {
    p.set('from', iso(q.from));
    p.set('to', iso(q.to));
  }
  const size = q.size ?? 50;
  p.set('limit', String(size));
  p.set('offset', String(((q.page ?? 1) - 1) * size));
  return req<HoldPage>('GET', `/api/v1/holds?${p.toString()}`);
}

/** Protects [from, to) of a device from retention; audited. */
export const createHold = (
  req: AuthedRequest,
  deviceId: string,
  from: number,
  to: number,
  reason: string,
  validDays: number,
) =>
  req<Hold>('POST', `/api/v1/devices/${encodeURIComponent(deviceId)}/holds`, {
    from: iso(from),
    to: iso(to),
    reason,
    valid_days: validDays,
  });

/** Gives the range back to retention (recordings.manage); audited. */
export const releaseHold = (req: AuthedRequest, id: string, reason: string) =>
  req<Hold>('POST', `/api/v1/holds/${encodeURIComponent(id)}/release`, { reason });

/** zip: original segments + SHA-256 manifest; mp4: one video cut to the range. */
export type ExportFormat = 'zip' | 'mp4';

export type ExportStatus = 'queued' | 'running' | 'ready' | 'failed' | 'expired';

/** Mirrors the export DTO of /api/v1/exports (ADR-015). */
export interface RecordingExport {
  id: string;
  device: { id: string; name: string };
  from: string;
  to: string;
  format: ExportFormat;
  reason: string;
  status: ExportStatus;
  error_code: string | null;
  created_by: { id: string; name: string };
  created_at: string;
  finished_at: string | null;
  expires_at: string | null;
  segments: number;
  size_bytes: number;
  /** SHA-256 of the delivered file, once ready. */
  sha256: string | null;
  file_name: string;
}

export interface ExportPage {
  items: RecordingExport[];
  total: number;
  limit: number;
  offset: number;
}

export const exportsKey = ['exports'] as const;

export function listExports(
  req: AuthedRequest,
  q: { page?: number; size?: number },
): Promise<ExportPage> {
  const size = q.size ?? 50;
  const p = new URLSearchParams({
    limit: String(size),
    offset: String(((q.page ?? 1) - 1) * size),
  });
  return req<ExportPage>('GET', `/api/v1/exports?${p.toString()}`);
}

/** Queues an export of [from, to) of a device; vms-worker builds it. Audited. */
export const createExport = (
  req: AuthedRequest,
  deviceId: string,
  from: number,
  to: number,
  format: ExportFormat,
  reason: string,
) =>
  req<RecordingExport>('POST', `/api/v1/devices/${encodeURIComponent(deviceId)}/exports`, {
    from: iso(from),
    to: iso(to),
    format,
    reason,
  });

/** A download URL valid for a few minutes; each one is audited. */
export const downloadLink = (req: AuthedRequest, id: string) =>
  req<{ url: string; expires_at: string }>(
    'POST',
    `/api/v1/exports/${encodeURIComponent(id)}/download`,
  );

export type CopyStatus = 'queued' | 'copying' | 'finishing' | 'done' | 'failed';

/** Mirrors the copy DTO of /api/v1/device-copies (ADR-042). */
export interface DeviceCopy {
  id: string;
  device: { id: string; name: string };
  from: string;
  to: string;
  reason: string;
  valid_days: number;
  created_by: { id: string; name: string };
  created_at: string;
  status: CopyStatus;
  /** no_replay, no_video or error when failed. */
  error_code: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** Segments stored so far. */
  segments: number;
  /** The evidence protecting the copy once done. */
  hold_id: string | null;
}

export const copiesKey = ['device-copies'] as const;

export const listCopies = (req: AuthedRequest) =>
  req<{ items: DeviceCopy[] }>('GET', '/api/v1/device-copies?limit=100');

/**
 * Copies [from, to) of the device's own recording into the platform, kept as
 * evidence for validDays (recordings.protect). Runs in real time, in the background.
 */
export const createCopy = (
  req: AuthedRequest,
  deviceId: string,
  from: number,
  to: number,
  reason: string,
  validDays: number,
) =>
  req<DeviceCopy>(
    'POST',
    `/api/v1/devices/${encodeURIComponent(deviceId)}/device-recordings/copy`,
    { from: iso(from), to: iso(to), reason, valid_days: validDays },
  );
